"use client";
import clsx from "clsx";
import type { SelectHTMLAttributes } from "react";
import { type Control, type FieldPath, type FieldValues, useController } from "react-hook-form";
import { ChevronDownIcon } from "../icons";

type Props<T extends FieldValues> = {
  label: string;
  name: FieldPath<T>;
  control: Control<T>;
  placeholder?: string;
  className?: string;
  selectClassName?: string;
  items: Array<{
    text: string;
    callbackValue: string;
  }>;
  inverse?: boolean;
} & SelectHTMLAttributes<HTMLSelectElement>;
export const Dropdown = <T extends FieldValues>({
  id,
  label,
  name,
  control,
  className,
  selectClassName,
  items,
  placeholder,
  inverse = false,
  ...props
}: Props<T>) => {
  const {
    field: { onBlur, onChange, ref, value },
    fieldState: { error },
  } = useController({ name, control });

  return (
    <div className={clsx("flex flex-col gap-1", className)}>
      {label && (
        <label htmlFor={id} className="text-xs/[14px] pl-2">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          id={id}
          value={value || ""}
          onChange={onChange}
          onBlur={onBlur}
          ref={ref}
          className={clsx(
            "appearance-none w-full h-12 pr-12 transition-colors hover:bg-bg-1/50 focus:bg-white focus:border-tx-2 outline-none rounded-lg border px-4 text-base/5 font-normal placeholder:text-tx-2 placeholder:font-normal placeholder:text-base/5",
            selectClassName,
            {
              "border-no bg-no/[0.02]": error,
              "border-bg-2": !error,
              "bg-bg-1": !error && !inverse,
              "bg-white": !error && inverse,
            },
          )}
          {...props}
        >
          <option value={undefined} hidden className="hidden" />
          {placeholder && (
            <option value="" hidden disabled className="hidden">
              {placeholder}
            </option>
          )}
          {items.map((item) => (
            <option key={item.callbackValue} value={item.callbackValue}>
              {item.text}
            </option>
          ))}
        </select>
        <ChevronDownIcon className="absolute top-[18px] right-4 pointer-events-none" />
      </div>
      {error?.message && <p className="text-no text-xs/[14px] pl-2">{error.message}</p>}
    </div>
  );
};
